import { PREVIEW_NS } from "@innoclouds/ui";
import { postToPreview } from "./mode";

let enabled = false;
let hovered: HTMLElement | null = null;
let box: HTMLElement | null = null;
let label: HTMLElement | null = null;
let frame = 0;

const INSERTS_STYLE_ID = "inno-preview-inspect-inserts";

const showInserts = (next: boolean) => {
	document.getElementById(INSERTS_STYLE_ID)?.remove();
	if (!next) return;
	const style = document.createElement("style");
	style.id = INSERTS_STYLE_ID;
	style.textContent = '[data-el^="ins_"] { pointer-events: auto !important; }';
	document.head.appendChild(style);
};

const ensureBox = () => {
	if (box?.isConnected && label) return;
	box = document.createElement("div");
	box.dataset.capture = "exclude";
	Object.assign(box.style, {
		position: "fixed",
		zIndex: "2147483647",
		pointerEvents: "none",
		outline: "max(2px, 0.3vw) solid #1360ea",
		background: "rgba(19, 96, 234, 0.1)",
		display: "none",
	});
	label = document.createElement("span");
	Object.assign(label.style, {
		position: "absolute",
		left: "0",
		padding: "0.2em 0.5em",
		background: "#1360ea",
		color: "#fff",
		font: "600 max(12px, 1.7vw)/1.3 ui-monospace, SFMono-Regular, Menlo, monospace",
		whiteSpace: "nowrap",
	});
	box.appendChild(label);
	document.body.appendChild(box);
};

const place = () => {
	frame = 0;
	if (!hovered || !box || !label) return;
	const rect = hovered.getBoundingClientRect();
	Object.assign(box.style, { display: "block", left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
	label.textContent = hovered.dataset.el ?? "";
	const inside = rect.top < 40;
	label.style.top = inside ? "0" : "auto";
	label.style.bottom = inside ? "auto" : "100%";
};

const schedule = () => {
	if (!frame) frame = requestAnimationFrame(place);
};

const hover = (element: HTMLElement | null) => {
	if (element === hovered) return;
	hovered = element;
	if (element) {
		ensureBox();
		schedule();
	} else if (box) box.style.display = "none";
	postToPreview({ ns: PREVIEW_NS, type: "hover", screen: element?.dataset.screen ?? null, element: element?.dataset.el ?? null, group: groupOf(element) });
};

const groupOf = (element: HTMLElement | null) => element?.closest<HTMLElement>("[data-group]")?.dataset.group ?? null;

const elementAt = (event: Event) => (event.target instanceof Element ? event.target.closest<HTMLElement>("[data-el]") : null);

const onOver = (event: MouseEvent) => hover(elementAt(event));

const onLeave = () => hover(null);

const BLOCKED = ["pointerdown", "pointerup", "mousedown", "mouseup", "touchstart", "touchend", "click"] as const;

const onPress = (event: Event) => {
	event.preventDefault();
	event.stopImmediatePropagation();
	if (event.type !== "click") return;
	const element = elementAt(event);
	if (element?.dataset.el)
		postToPreview({ ns: PREVIEW_NS, type: "pick", screen: element.dataset.screen ?? null, element: element.dataset.el, group: groupOf(element) });
};

const isEditable = (target: EventTarget | null) =>
	target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

export const listenInspectToggle = () =>
	window.addEventListener(
		"keydown",
		(event) => {
			if (event.code !== "Space" || event.repeat || isEditable(event.target)) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			postToPreview({ ns: PREVIEW_NS, type: "inspect-toggle" });
		},
		true,
	);

export const setInspect = (next: boolean) => {
	if (next === enabled) return;
	enabled = next;
	showInserts(next);

	if (next) {
		document.addEventListener("mouseover", onOver, true);
		document.addEventListener("mousemove", schedule, true);
		document.documentElement.addEventListener("mouseleave", onLeave);
		window.addEventListener("scroll", schedule, true);
		for (const type of BLOCKED) window.addEventListener(type, onPress, { capture: true, passive: false });
		return;
	}

	document.removeEventListener("mouseover", onOver, true);
	document.removeEventListener("mousemove", schedule, true);
	document.documentElement.removeEventListener("mouseleave", onLeave);
	window.removeEventListener("scroll", schedule, true);
	for (const type of BLOCKED) window.removeEventListener(type, onPress, { capture: true });
	hover(null);
};
